// ============================================================================
// VALIDATE SIGNALS — concurrent backtest runner
//
// Runs `backtestSignal` over a batch of signals with bounded concurrency, so
// every signal that appears on a page is data-backed by default. The caller
// gets per-signal updates via the onProgress callback as each backtest settles,
// which enables progressive UI reveal (cards flip from "VALIDATING…" to
// "VALIDATED" / "LIMITED DATA" individually).
//
// Concurrency: 12. Polygon has no rate limits, but cranking concurrency
// arbitrarily high triggers a different failure mode — every settled backtest
// fires a setSignals(prev => prev.map(...)) callback, and bursting 25+ of
// those in parallel can starve the React render loop. 12 is the sweet spot:
// 2x faster than the original 6, while leaving the UI responsive.
// ============================================================================

import { backtestSignal } from './backtestEngine';

const DEFAULT_CONCURRENCY = 12;

// onProgress: ({ ticker, status, result, done, total, cancelled }) => void
//   status: 'validated' | 'insufficient' | 'error'
//   result: backtest result object when status='validated', else null
export async function validateSignalsBatch(signals, onProgress, opts = {}) {
  const concurrency = opts.concurrency ?? DEFAULT_CONCURRENCY;
  const isCancelled = opts.isCancelled || (() => false);

  // HOLD signals don't need a backtest — there's no trade thesis to validate.
  const queue = signals.filter(s => s.direction === 'LONG' || s.direction === 'SHORT');
  const total = queue.length;
  if (total === 0) return;

  let cursor = 0;
  let done   = 0;

  async function runOne(signal) {
    if (isCancelled()) return;
    let status, result = null;
    try {
      const r = await backtestSignal(signal);
      if (r) {
        status = 'validated';
        result = r;
      } else {
        status = 'insufficient';
      }
    } catch (e) {
      console.error(`[ValidateBatch] ${signal.ticker} failed:`, e?.message || e);
      status = 'error';
    }
    done++;
    if (!isCancelled()) {
      onProgress({ ticker: signal.ticker, status, result, done, total });
    }
  }

  async function worker() {
    while (cursor < queue.length && !isCancelled()) {
      const i = cursor++;
      await runOne(queue[i]);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, total) }, () => worker());
  await Promise.all(workers);
}
